import React, { createContext, useEffect, useState } from "react";

const UserContext = createContext();
const TOKEN_STORAGE_KEY = "authToken";
const REFRESH_TOKEN_STORAGE_KEY = "refreshToken";
const USER_STORAGE_KEY = "user";

function getStoredUser() {
    try {
        const storedUser = localStorage.getItem(USER_STORAGE_KEY);
        return storedUser ? JSON.parse(storedUser) : null;
    } catch (error) {
        return null;
    }
}

const UserContextProvider = ({ children }) => {
    const [user, setUser] = useState(getStoredUser);
    const [error, setError] = useState(null);
    const [isAuthReady, setIsAuthReady] = useState(false);

    useEffect(() => {
        bootstrapAuth();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function clearSession() {
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
        localStorage.removeItem(USER_STORAGE_KEY);
        setUser(null);
    }

    async function refreshAccessToken() {
        const refreshToken = localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
        if (!refreshToken) {
            return null;
        }
        const response = await fetch(`${process.env.REACT_APP_API_URI}/api/auth/refresh`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ refreshToken }),
        });
        if (!response.ok) {
            throw new Error("Refresh failed.");
        }
        const data = await response.json();
        localStorage.setItem(TOKEN_STORAGE_KEY, data.token);
        if (data.refreshToken) {
            localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, data.refreshToken);
        }
        if (data.user) {
            localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(data.user));
            setUser(data.user);
        }
        return data.token;
    }

    async function bootstrapAuth() {
        let token = localStorage.getItem(TOKEN_STORAGE_KEY);
        if (!token) {
            clearSession();
            setIsAuthReady(true);
            return;
        }

        try {
            let response = await fetch(`${process.env.REACT_APP_API_URI}/api/auth/me`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            if (response.status === 401) {
                token = await refreshAccessToken();
                if (!token) {
                    throw new Error("Session expired.");
                }
                response = await fetch(`${process.env.REACT_APP_API_URI}/api/auth/me`, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
            }
            if (!response.ok) {
                throw new Error("Session expired.");
            }
            const data = await response.json();
            setUser(data.user);
            localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(data.user));
        } catch (bootstrapError) {
            clearSession();
        } finally {
            setIsAuthReady(true);
        }
    }

    const loginHandler = async (email, password) => {
        setError(null);
        const response = await fetch(`${process.env.REACT_APP_API_URI}/api/auth/login`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                email: email.toLowerCase(),
                password,
            }),
        });

        let data = {};
        try {
            data = await response.json();
        } catch (parseError) {
        }

        if (!response.ok) {
            const msg = data?.message || "Login failed.";
            setError(msg);
            throw new Error(msg);
        }

        localStorage.setItem(TOKEN_STORAGE_KEY, data.token);
        if (data.refreshToken) {
            localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, data.refreshToken);
        }
        localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(data.user));
        setUser(data.user);
        return data.user;
    };

    const logoutHandler = async () => {
        const token = localStorage.getItem(TOKEN_STORAGE_KEY);
        try {
            if (token) {
                await fetch(`${process.env.REACT_APP_API_URI}/api/auth/logout`, {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
            }
        } catch (logoutError) {
            console.error(logoutError);
        } finally {
            clearSession();
        }
    };

    return (
        <UserContext.Provider value={{
            user,
            setUser,
            onLogout: logoutHandler,
            onLogin: loginHandler,
            error,
            setError,
            isAuthReady,
        }}>
            {children}
        </UserContext.Provider>
    );
};

export { UserContext, UserContextProvider };
